import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useDashboardDataProvider } from 'features/dashboard'
import { createRsrcKey } from 'features/resources'
import { SessionActivityCard } from 'procyon-ui'
import UsersResourcesActivitySkeleton from './UsersResourcesActivitySkeleton'

/* ------------------------------
 * Constants
 * ------------------------------ */
const YEAR_DISPLAY_FORMAT = 'MMM YYYY'
const THIRTY_DAYS_DISPLAY_FORMAT = 'DD MMM'
const SEVEN_DAYS_DISPLAY_FORMAT = 'DD MMM'
const TWENTY_FOUR_HOUR_DISPLAY_FORMAT = 'HH:00 A, DD MMM'

const EMPTY_DATASET = {
  '12m': [],
  '24h': [],
  '30d': [],
  '7d': []
}

/* ------------------------------
 * Date buckets (static)
 * ------------------------------ */
const LAST_12_MONTHS = []
let i = 11
while (i >= 0) {
  LAST_12_MONTHS.push(moment().subtract(i, 'months').format(YEAR_DISPLAY_FORMAT))
  i--
}

const LAST_30_DAYS = []
i = 30
while (i >= 0) {
  LAST_30_DAYS.push(moment().subtract(i, 'days').format(THIRTY_DAYS_DISPLAY_FORMAT))
  i--
}

const LAST_7_DAYS = []
i = 7
while (i >= 0) {
  LAST_7_DAYS.push(moment().subtract(i, 'days').format(SEVEN_DAYS_DISPLAY_FORMAT))
  i--
}

const LAST_24_HOURS_DATES_GROUPS = []
i = 24
while (i >= 0) {
  LAST_24_HOURS_DATES_GROUPS.push(
    moment().subtract(i, 'hours').format(TWENTY_FOUR_HOUR_DISPLAY_FORMAT)
  )
  i--
}

/* ------------------------------
 * Helpers
 * ------------------------------ */

const buildSeries = (keys, map) =>
  keys.flatMap((time) => [
    { name: 'Users', time, amount: map.users[time]?.count || 0 },
    { name: 'Resources', time, amount: map.resources[time]?.count || 0 }
  ])

const mapKeyFormat = {
  1: {
    diffUnit: 'hours',
    diffValue: 24,
    format: TWENTY_FOUR_HOUR_DISPLAY_FORMAT,
    keys: LAST_24_HOURS_DATES_GROUPS
  }, // 24h
  7: { diffUnit: 'days', diffValue: 7, format: SEVEN_DAYS_DISPLAY_FORMAT, keys: LAST_7_DAYS }, // 7d
  30: { diffUnit: 'days', diffValue: 30, format: THIRTY_DAYS_DISPLAY_FORMAT, keys: LAST_30_DAYS }, // 30d
  60: { diffUnit: 'days', diffValue: 60, format: THIRTY_DAYS_DISPLAY_FORMAT, keys: LAST_30_DAYS }, // Note: 60 days maps to 30d format/keys currently
  365: { diffUnit: 'months', diffValue: 12, format: YEAR_DISPLAY_FORMAT, keys: LAST_12_MONTHS } // 12m
}

const processHeatMapForTimeRange = (heatMap, startDaysValue) => {
  const config = mapKeyFormat[startDaysValue]
  if (!config) return { timeMap: { users: {}, resources: {} }, keys: [] } // Return empty if no config

  const { diffUnit, diffValue, format, keys } = config

  const timeMap = { users: {}, resources: {} }
  const groups = [Object.values(heatMap.users), Object.values(heatMap.resources)]
  const now = moment()

  groups.forEach((items, index) => {
    const rsrcOrUserKey = index === 0 ? 'users' : 'resources'

    items.forEach((data) => {
      const { user, rsrc, eventTimes } = data
      const refObj = rsrcOrUserKey === 'users' ? user : rsrc

      eventTimes.forEach((eventTime) => {
        const mmt = moment(eventTime)

        if (now.diff(mmt, diffUnit) < diffValue) {
          const timeKey = mmt.format(format)
          const existing = timeMap[rsrcOrUserKey][timeKey]
          timeMap[rsrcOrUserKey][timeKey] = {
            count: (existing?.count || 0) + 1,
            time: timeKey,
            dataKeys: [...(existing?.dataKeys || []), createRsrcKey(refObj)]
          }
        }
      })
    })
  })

  return { timeMap, keys }
}

/* ------------------------------
 * Component
 * ------------------------------ */
const UsersResourcesActivity = () => {
  const { heatMap, startDays, overallLoading, dashboardReady } = useDashboardDataProvider()

  const [stableDataSet, setStableDataSet] = useState(EMPTY_DATASET)
  const hasAnimatedRef = useRef(false)

  /* ------------------------------
   * Data processing lifecycle
   * ------------------------------ */
  useEffect(() => {
    if (dashboardReady && heatMap && startDays) {
      const { timeMap, keys } = processHeatMapForTimeRange(heatMap, startDays)
      const viewKey = viewMap[startDays]

      setStableDataSet((prev) => ({
        ...EMPTY_DATASET,
        [viewKey]: buildSeries(keys, timeMap)
      }))

      if (!hasAnimatedRef.current) {
        hasAnimatedRef.current = true
      }
    } else {
      // Reset when loading or when data is not present
      setStableDataSet(EMPTY_DATASET)
      hasAnimatedRef.current = false
    }
  }, [dashboardReady, heatMap, startDays]) // Added startDays to dependency array

  /* ------------------------------
   * Render
   * ------------------------------ */

  const viewMap = {
    1: '24h',
    7: '7d',
    30: '30d',
    60: '30d',
    365: '12m'
  }

  const activeView = viewMap[startDays] || '7d'

  return (
    <div className='mt-8'>
      <SessionActivityCard
        title='Activity'
        caption='active users vs resources accessed'
        dataSet={stableDataSet}
        view={activeView}
        animate={!hasAnimatedRef.current}
      />
    </div>
  )
}

export { UsersResourcesActivity }
